import { CustomAttributeType } from "@eu.mdotm.api/custom-attributes";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type ButtonGroupOption,
	ButtonGroupRadio,
	Column,
	Icon,
	Row,
	type StylableProps,
	Transition,
} from "@mdotm/shadcn/legacy/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, useAtomValue, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type DataAttributesProps, Button } from "@mdotm/shadcn/mdotm/components";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import { Map } from "immutable";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { SidePanelContent, SidePanelFooter, SidePanelHeader } from "$root/components/common/SidePanelV2";
import { StackContextBlock } from "$root/components/common/Stack";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { Divider } from "$root/components/Divider";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueriesWrapperBase, ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { investmentCustomAttributesRepository } from "$root/functional-areas/customAttributes/repository";
import type { CustomAttributeDto } from "$root/functional-areas/customAttributes/type";
import { platformToast } from "$root/notification-system/toast";
import { handleSubmitToCustomSubmitHandler } from "$root/third-party-integrations/react-hook-form";
import { DraggableSettingsPanel } from "$root/ui-lib/DraggableSettingsPanel";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { qualifier } from "$root/utils";
import { ChainableElement, WorkspaceChartChain } from "../chain/index";
import { autoGenerateMetricName } from "../common";
import { conceptFormModel, conceptFormSubmitSchema, formFromConcept } from "../concept-form";
import { workspaceRepository } from "../repository";
import { isCustomAttributeConcept } from "../services/sharedConceptService";
import { spawnDeleteCustomAttributeDialog, spawnDeleteSharedConceptDialog } from "../spawn-delete-column-dialog";
import { spawnDeleteTagOptionDialog } from "../spawn-delete-tag-option-dialog";
import { spawnRenameTagOptionDialog } from "../spawn-rename-tag-option-dialog";
import { selectIsShared, useWorkspaceDispatch, useWorkspaceSlices, useWorkspaceStore } from "../state";
import { isManagedConcept } from "../spec-gaps";
import { SidePanelListItem } from "../ui/SidePanelItem";
import type { SubmittableHandle } from "./type";
import WorkspaceTableSettingsColumnAdd from "./WorkspaceTableSettingsColumnAdd";
import WorkspaceTableSettingsColumnProperty, {
	type WorkspaceTableSettingsColumnProperty as WorkspaceTableSettingsColumnPropertyType,
} from "./WorkspaceTableSettingsColumnProperty";

export function WorkspaceTableSettingStack() {
	const slices = useWorkspaceSlices();
	const dispatch = useWorkspaceDispatch();
	const stackCtx = useMandatoryContext(StackContextBlock);
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const workspaceConceptsMap = workspaceRepository.getConceptMapDefinitions.useQuery();
	const workspaceTagConcepts = workspaceRepository.getTagConceptDefinitions.useQuery();
	const allParametrizedConcepts = useAtomValue(slices.concepts$);
	const parametrizedConceptsMap = useMemo(
		() => Map(allParametrizedConcepts.map((parametrized) => [parametrized.uuid, parametrized])),
		[allParametrizedConcepts],
	);

	const columns = readAtom(slices.columns$);
	if (!columns?.length) {
		return (
			<>
				<SidePanelHeader
					title="Table Settings"
					primaryAction={{
						label: "Add column",
						onClick() {
							stackCtx.push(() => <WorkspaceCreateTableSettingStack title="Add column" />);
						},
					}}
					icon={{
						name: "Left",
						onClick() {
							stackCtx.pop();
						},
					}}
					description="Manage columns and table display options"
				/>
				<Divider />
				<SidePanelContent className="flex-1">
					<IconWalls.WorkspaceMissingColumns
						onClick={() => sidePanelCtx.push(() => <WorkspaceCreateTableSettingStack title="Add column" />)}
					/>
				</SidePanelContent>
			</>
		);
	}

	return (
		<ReactQueriesWrapperBase queries={[workspaceConceptsMap, workspaceTagConcepts]}>
			{([conceptsMap, availableTagsConcepts]) => (
				<WithAtom atom$={slices.columns$}>
					{(columnSettings) => (
						<DraggableSettingsPanel
							title="Table Settings"
							description="Manage columns and table display options"
							primaryAction={{
								label: "Add column",
								onClick() {
									stackCtx.push(() => <WorkspaceCreateTableSettingStack title="Add column" />);
								},
							}}
							onBack={{
								icon: "Left",
								action() {
									stackCtx.pop();
								},
							}}
							items={columnSettings ?? []}
							itemKey={(item) => item.colId}
							onReorder={(columns) => void dispatch({ type: "reorderColumns", columns })}
							searchExtract={(item) => parametrizedConceptsMap.get(item.colId)?.label ?? ""}
							emptyState={
								<IconWalls.WorkspaceMissingColumns
									onClick={() => sidePanelCtx.push(() => <WorkspaceCreateTableSettingStack title="Add column" />)}
								/>
							}
						>
							{(colSettings, { dragIcon, isDragging }) => {
								// `conceptReferencesResolve` is enforced, so every persisted column names a concept the pool
								// holds — the row that rendered nothing was guarding a document ingestion now repairs.
								const parametrizedConcept = parametrizedConceptsMap.get(colSettings.colId) ?? unreachable();
								return (
									<Column className="overflow-hidden">
										<Row gap={8} alignItems="center">
											{dragIcon}
											<SidePanelListItem
												disableToggle={parametrizedConcept.conceptId === "systemId: name" && !colSettings.hide}
												checked={!colSettings.hide}
												onToggle={(visible) => {
													void dispatch({
														type: "reorderColumns",
														columns:
															columnSettings?.map((col) =>
																col.colId === colSettings.colId ? { ...col, hide: !visible } : col,
															) ?? null,
													});
												}}
												title={parametrizedConcept?.label ?? "-"}
												onDrillDown={() => {
													sidePanelCtx.push(() => (
														<WorkspaceEditTableSettingsStack
															id={parametrizedConcept.uuid}
															title={parametrizedConcept.label}
															initialData={formFromConcept(
																parametrizedConcept,
																conceptsMap.get(parametrizedConcept.conceptId),
																{ tagConcepts: availableTagsConcepts },
															)}
														/>
													));
												}}
											/>
										</Row>
										{!isDragging && <Divider className="border-dashed" />}
									</Column>
								);
							}}
						</DraggableSettingsPanel>
					)}
				</WithAtom>
			)}
		</ReactQueriesWrapperBase>
	);
}

/**
 * The column panels' form model: a chain, the name checkbox, and a description of the column.
 *
 * Derived from the model like every form type in this area — the resolver is built by extending it, so it
 * cannot fail to declare one of these fields. It used to be `workspaceChainSchema` alone, which declared
 * neither `description` nor `autoGenerateName` and therefore deleted both on the way to submit.
 */
const workspaceTableSettingsFormModel = conceptFormModel.extend({
	description: z.string().optional(),
});

export type WorkspaceTableSettingsDetails = z.infer<typeof workspaceTableSettingsFormModel>;

/** What an edited column has to answer at submit time. The chain is the whole of it. */
export const workspaceTableSettingsResolverSchema = conceptFormSubmitSchema(workspaceTableSettingsFormModel);

/** @internal — exported for tests */
export function customAttributeDtoToColumnProperty(dto: CustomAttributeDto): WorkspaceTableSettingsColumnPropertyType {
	if (dto.attributeType === CustomAttributeType.Tag) {
		return {
			attributeType: CustomAttributeType.Tag,
			name: dto.name ?? "",
			description: dto.description,
			tagOptions:
				dto.tags?.map((tag) => ({
					label: tag.value ?? "",
					color: tag.color ?? "GRAY",
				})) ?? [],
			allowMultipleTags: dto.multiTag ?? false,
		};
	}

	const attributeType =
		dto.attributeType === CustomAttributeType.Date
			? CustomAttributeType.Date
			: dto.attributeType === CustomAttributeType.Number
				? CustomAttributeType.Number
				: CustomAttributeType.Text;

	return {
		attributeType,
		name: dto.name ?? "",
		description: dto.description,
	};
}

export function WorkspaceEditTableSettingsStack(props: {
	id: string;
	title: string;
	description?: string;
	initialData?: Partial<WorkspaceTableSettingsDetails>;
}) {
	if (props.initialData?.concept?.dataType && isCustomAttributeConcept(props.initialData.concept.dataType)) {
		return <WorkspaceEditCustomAttributeStack {...props} />;
	}

	return <WorkspaceEditConceptStack {...props} />;
}

function WorkspaceEditCustomAttributeStack(props: {
	id: string;
	title: string;
	description?: string;
	initialData?: Partial<WorkspaceTableSettingsDetails>;
}) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const dispatch = useWorkspaceDispatch();
	const propertyRef = useRef<SubmittableHandle>(null);

	// System-shared (managed) concepts: configuration is read-only — no Update, no Delete.
	const isManaged = isManagedConcept(props.initialData?.concept);

	const customAttributeUuid = props.initialData?.concept?.id?.substring("customAttributeId: ".length - 1).trim();
	const customAttributeQuery = investmentCustomAttributesRepository.getCustomAttribute.useQuery(
		customAttributeUuid ?? "",
	);

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={
					isManaged
						? undefined
						: {
								label: "Update",
								async onClickAsync() {
									await propertyRef.current?.submit();
								},
								disabled: customAttributeQuery.isLoading || customAttributeQuery.isError,
							}
				}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<Column minSize="0" gap={16}>
					<ChainableElement.SelectConcept value={props.initialData?.concept?.id ?? null} disabled onChange={() => {}} />
					<Divider />
					<ReactQueryWrapperBase query={customAttributeQuery}>
						{(customAttribute) => {
							const defaultValues = customAttributeDtoToColumnProperty(customAttribute);

							return (
								<WorkspaceTableSettingsColumnProperty
									ref={propertyRef}
									defaultValues={defaultValues}
									readonly={isManaged}
									readonlyAttributeType
									readonlyAllowMultipleTags
									onRenameTagOption={async (currentTagValue, newTagValue) => {
										if (!customAttributeUuid || defaultValues.attributeType !== "TAG") {
											throw new Error("Cannot rename a tag option outside a tag custom attribute");
										}

										const usage = await investmentCustomAttributesRepository.verifyTagOptionUsage.fetchQuery({
											attributeUuid: customAttributeUuid,
											tagValue: currentTagValue,
										});

										const confirmed = await spawnRenameTagOptionDialog({
											currentTagValue,
											newTagValue,
											investmentCount: usage.investmentCount ?? 0,
											filterCount: usage.filterCount ?? 0,
										});

										// Rejecting is how the editor is told to leave the option alone — see `onRenameOption`. Cancelling
										// is not a failure, so it rejects without reporting; the write below reports and rethrows, which
										// reverts the row for a name the server refused (409) or a propagation that rolled back.
										if (!confirmed) {
											throw new Error("Tag option rename cancelled");
										}

										await runWithErrorReporting(
											async () => {
												await dispatch({
													type: "renameTagOption",
													parameterizedConceptUuid: props.id,
													currentTagValue,
													newTagValue,
												});
												platformToast({
													severity: "success",
													children: "Tag option renamed successfully!",
													icon: "Icon-full-ok",
												});
											},
											{
												area: "workspace",
												attemptedOperation: `try to rename a tag option of custom attribute for concept-uuid ${props.id}`,
											},
										);
									}}
									onBeforeRemoveTagOption={async (optionToRemove, form) => {
										if (!customAttributeUuid) {
											return false;
										}

										if (defaultValues.attributeType !== "TAG") {
											return false;
										}

										const result = await investmentCustomAttributesRepository.verifyTagOptionUsage.fetchQuery({
											attributeUuid: customAttributeUuid,
											tagValue: optionToRemove.option ?? "",
										});

										if (!result.inUse) {
											return true;
										}

										const confirmed = await spawnDeleteTagOptionDialog({
											investmentCount: result.investmentCount ?? 0,
										});

										if (!confirmed) {
											return false;
										}

										// The dispatch is a write, so it can fail against the network. Reporting it is what turns a
										// failure into a toast the user can read; the rethrow then skips the `return true`, so the
										// option whose removal did not persist stays on screen.
										await runWithErrorReporting(
											async () => {
												const newTagOptions = form
													.getValues("tagOptions")
													?.filter((opt) => opt.label !== optionToRemove.option);

												await dispatch({
													type: "editColumn",
													parameterizedConceptUuid: props.id,
													property: {
														...defaultValues,
														tagOptions: newTagOptions ?? [],
													},
												});
											},
											{
												area: "workspace",
												attemptedOperation: `try to remove a tag option from custom attribute for concept-uuid ${props.id}`,
											},
										);

										return true;
									}}
									onSubmit={async (property) => {
										await runWithErrorReporting(
											async () => {
												await dispatch({
													type: "editColumn",
													parameterizedConceptUuid: props.id,
													property,
												});
												platformToast({
													severity: "success",
													children: "Custom attribute updated successfully!",
													icon: "Icon-full-ok",
												});
												sidePanelCtx.pop();
											},
											{
												area: "workspace",
												attemptedOperation: `try to update custom attribute for concept-uuid ${props.id}`,
											},
										);
									}}
								/>
							);
						}}
					</ReactQueryWrapperBase>
				</Column>
			</SidePanelContent>
			{isManaged ? null : (
				<SidePanelFooter
					className={"z-10"}
					neutralAction={() => {
						return (
							<Button
								palette="secondary"
								size="small"
								className="gap-1"
								disabled={customAttributeQuery.isLoading || customAttributeQuery.isError}
								onClickAsync={() =>
									runWithErrorReporting(
										async () => {
											const result = await workspaceRepository.getParametrizedConceptUserUsage.fetchQuery([props.id]);
											const usage = result?.conceptUsages?.[0];
											const workspaceUsages = usage?.workspaceUsages ?? [];
											const otherUsersCount = usage?.otherUsersUsing?.acl?.length ?? 0;

											if (workspaceUsages.length > 0 || otherUsersCount > 0) {
												const confirmed = await spawnDeleteCustomAttributeDialog();
												if (!confirmed) {
													return;
												}
											}

											await dispatch({
												type: "removeColumn",
												parameterizedConceptUuid: props.id,
												shared: true,
											});
											sidePanelCtx.pop();
										},
										{
											area: "workspace",
											attemptedOperation: `try to delete custom attribute column as concept-uuid ${props.id}`,
										},
									)
								}
								data-qualifier={qualifier.component.button("Delete")}
							>
								<Icon icon="Delete" size={16} /> Delete
							</Button>
						);
					}}
				/>
			)}
		</>
	);
}

function WorkspaceEditConceptStack(props: {
	id: string;
	title: string;
	description?: string;
	initialData?: Partial<WorkspaceTableSettingsDetails>;
}) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const { dispatch, readState } = useWorkspaceStore();

	// Safety net: managed concepts are always custom attributes and should route to the custom
	// attribute stack, but if `dataType` is missing they may land here — still no Update/Delete.
	const isManaged = isManagedConcept(props.initialData?.concept);

	const form = useForm<WorkspaceTableSettingsDetails>({
		defaultValues: { autoGenerateName: true, ...(props.initialData ?? {}) },
		resolver: zodResolver(workspaceTableSettingsResolverSchema),
	});

	const autoGenerateName = form.watch("autoGenerateName");

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={
					isManaged
						? undefined
						: {
								label: "Update",
								async onClickAsync() {
									await handleSubmitToCustomSubmitHandler({
										handleSubmit: form.handleSubmit,
										baseSubmitFn: async (workspaceConcept) => {
											await runWithErrorReporting(
												async () => {
													// Where the concept lives is a property of the payload now, not a choice of method.
													await dispatch({
														type: "editColumn",
														parameterizedConceptUuid: props.id,
														chain: workspaceConcept,
														shared: selectIsShared(readState(), props.id),
													});
													// Inside, as the success continuation: a failed update has to leave the panel open on the
													// values the user typed, so the toast has something to send them back to.
													sidePanelCtx.pop();
												},
												{
													area: "workspace",
													attemptedOperation: `try to update column for concept-uuid ${props.id}`,
												},
											);
										},
									})();
								},
							}
				}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<Column gap={16}>
					<div className="grid grid-cols-2 grid-rows-1 gap-x-4 gap-y-2 items-end ">
						<WorkspaceChartChain
							initialValue={props.initialData}
							autogenerateName={autoGenerateName}
							onChange={(newWorkspaceChain) => {
								form.reset({
									...newWorkspaceChain,
									autoGenerateName: form.getValues("autoGenerateName"),
								});
							}}
						/>
					</div>
					<Divider />
					<Column gap={8}>
						<FormFields.Text
							name="name"
							control={form.control}
							formState={form.formState}
							label="Name"
							disabled={autoGenerateName}
						/>
						<FormFields.Checkbox
							name="autoGenerateName"
							control={form.control}
							formState={form.formState}
							className="flex-row-reverse gap-2 w-fit"
							onChange={(shouldAutogenerate) => {
								if (shouldAutogenerate) {
									form.reset({
										...form.getValues(),
										name: autoGenerateMetricName(form.getValues()),
									});
								}
							}}
						>
							Auto-generated
						</FormFields.Checkbox>
					</Column>
					{/* <FormFields.TextArea
						name="description"
						control={form.control}
						formState={form.formState}
						label="Description"
						placeholder="Add a free description to this item. The text will be displayed in the lis"
					/> */}
				</Column>
			</SidePanelContent>
			{isManaged ? null : (
				<SidePanelFooter
					className={"z-10"}
					neutralAction={() => {
						return (
							<Button
								palette="secondary"
								size="small"
								className="gap-1"
								onClickAsync={() =>
									runWithErrorReporting(
										async () => {
											const isShared = selectIsShared(readState(), props.id);

											if (isShared) {
												const result = await workspaceRepository.getParametrizedConceptUserUsage.fetchQuery([props.id]);
												const workspaceUsages = result?.conceptUsages?.[0]?.workspaceUsages ?? [];

												if (workspaceUsages.length > 0) {
													const confirmed = await spawnDeleteSharedConceptDialog();
													if (!confirmed) {
														return;
													}
												}
											}

											await dispatch({
												type: "removeColumn",
												parameterizedConceptUuid: props.id,
												shared: isShared,
											});

											sidePanelCtx.pop();
										},
										{
											area: "workspace",
											attemptedOperation: `try to delete column as concept-uuid ${props.id}`,
										},
									)
								}
								data-qualifier={qualifier.component.button("Delete")}
							>
								<Icon icon="Delete" size={16} /> Delete
							</Button>
						);
					}}
				/>
			)}
		</>
	);
}

export function WorkspaceCreateTableSettingStack(props: { title: string; description?: string }) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const dispatch = useWorkspaceDispatch();
	const [createColumnMode, setCreateColumnMode] = useState<CreateColumnMode>("CREATE_COLUMN");

	const addColumnRef = useRef<SubmittableHandle>(null);
	const createPropertyRef = useRef<SubmittableHandle>(null);

	const activeRef = useMemo(
		() => (createColumnMode === "CREATE_COLUMN" ? addColumnRef : createPropertyRef),
		[createColumnMode],
	);

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={{
					label: "Apply",
					async onClickAsync() {
						await activeRef.current?.submit();
					},
				}}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<ButtonGroupRadio
					className="mb-2"
					options={createColumnModeOptions}
					value={createColumnMode}
					onChange={setCreateColumnMode}
					size="small"
				/>
				<Divider className="mb-4" />
				<div className="grid grid-cols-1 grid-rows-[minmax(0,1fr)] min-h-0 flex-1">
					<Transition
						className="transition-opacity col-start-1 row-start-1"
						enterToClassName="opacity-100"
						enterFromClassName="opacity-0"
						exitedClassName="invisible"
						in={createColumnMode === "CREATE_COLUMN"}
					>
						{(transitionProps) => (
							<div
								className={overrideClassName(transitionProps.className, "min-h-0 flex flex-col")}
								style={transitionProps.style}
							>
								<WorkspaceTableSettingsColumnAdd
									ref={addColumnRef}
									onSubmit={async (workspaceConcept) => {
										await runWithErrorReporting(
											async () => {
												// One operation; `share` says where the concept lands.
												await dispatch({
													type: "addColumn",
													chain: workspaceConcept,
													share: workspaceConcept.concept?.shareable,
												});

												platformToast({
													severity: "success",
													children: "Column created successfully!",
													icon: "Icon-full-ok",
												});
												sidePanelCtx.pop();
											},
											{
												area: "workspace",
												attemptedOperation: "try to add a new column",
											},
										);
									}}
								/>
							</div>
						)}
					</Transition>
					<Transition
						className="transition-opacity col-start-1 row-start-1"
						enterToClassName="opacity-100"
						enterFromClassName="opacity-0"
						exitedClassName="invisible"
						in={createColumnMode === "CREATE_PROPERTY"}
					>
						{(transitionProps) => (
							<div
								className={overrideClassName(transitionProps.className, "min-h-0 flex flex-col")}
								style={transitionProps.style}
							>
								<WorkspaceTableSettingsColumnProperty
									ref={createPropertyRef}
									onSubmit={async (property) => {
										await runWithErrorReporting(
											async () => {
												await dispatch({ type: "addColumn", property });
												platformToast({
													severity: "success",
													children: "Custom attribute created successfully!",
													icon: "Icon-full-ok",
												});
												sidePanelCtx.pop();
											},
											{
												area: "workspace",
												attemptedOperation: "try to create a new property",
											},
										);
									}}
								/>
							</div>
						)}
					</Transition>
				</div>
			</SidePanelContent>
		</>
	);
}
type CreateColumnMode = "CREATE_COLUMN" | "CREATE_PROPERTY";
const createColumnModeOptions: Array<ButtonGroupOption<CreateColumnMode> & StylableProps & DataAttributesProps> = [
	{
		value: "CREATE_COLUMN",
		children: "Existing property",
	},
	{
		value: "CREATE_PROPERTY",
		children: "Create new property",
	},
];
